/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import axios, { AxiosError } from 'axios'
import { getReviewsByProductId } from '@api/apiCalls'
import {
  CONTENTFUL_HEADERS_ID,
  PRODUCT_TYPES_WITHOUT_COLORS
} from '@consts/index'
import { getProductCategory } from '@helpers/products'
import { getValueFromObject } from '@helpers/utils'
import { isProdEnv } from '@helpers/env'
import logBuildError from '@helpers/misc/logBuildError'
import { transformProductOptions } from '@helpers/products/transformProductOptions'
import {
  fetchContentfulProductsBreakdownByProductCategory,
  fetchContentfulProductsBreakdownById
} from '@api/graphqlContentFul/productsBreakdown'
import {
  fetchProductDiscountDescription,
  fetchProductCareCopy as fetchContenfulProductCareCopy
} from '@api/graphqlContentFul'
import { searchDataByAttribute } from '@helpers/contentful'
import ProductList, { ProductListProps } from '@components/product/ProductList'
import { useRouter } from 'next/router'
import {
  ContentfulDocument,
  ProductDiscountItem,
  Storytelling
} from 'types/contentful/graphQlApi'
import { EmptyObject } from 'types/helpers'
import { ProductByType, ProductHandles } from 'types/product'
import { default as NextError } from 'next/error'
import { CustomError } from '@helpers/errors'
import {
  getProductHandles,
  getProductsByType
} from '../../server/api/graphqlContentService/products'
import { formatGroupType, yotpoRatingsHandler } from '@api/yotpo/ratings'

const GiftCard = dynamic(() => import('@components/product/GiftCard'))
const NotFound = dynamic(() => import('../404'))

type DiscountByProductType = Record<
  string,
  { title: string; description: string } | EmptyObject
>

const handleProductsBuildError = logBuildError('products')

async function getProductsBreakdown(
  productType: string,
  productCategory: string,
  preview = false
) {
  if (productType === 'Gift Card') {
    const { productsBreakdown } = (await fetchContentfulProductsBreakdownById({
      id: CONTENTFUL_HEADERS_ID.productBreakdownGiftingCollection,
      preview
    })) || { productsBreakdown: { items: [] } }
    return productsBreakdown
  }

  const { productsBreakdown } =
    (await fetchContentfulProductsBreakdownByProductCategory({
      productCategory,
      preview
    })) || { productsBreakdown: { items: [] } }

  return productsBreakdown?.items[0]
}

async function fetchStorytelling({
  productTypes,
  handle,
  isPreview = false
}: {
  productTypes: string[]
  handle: string
  isPreview?: boolean
}) {
  const productTypesStorytelling: Record<string, Storytelling[] | null> = {}

  for (const productType of productTypes) {
    const dashedProductType = productType.replace(/\s/g, '-').toLowerCase()
    await searchDataByAttribute<{ content: Record<string, Storytelling> }>(
      {
        content_type: 'storytelling',
        'fields.productType': dashedProductType
      },
      isPreview
    )
      .then(response => {
        productTypesStorytelling[productType] = response?.content
          ? Object.values(response.content)
          : null
      })
      .catch(error => {
        handleProductsBuildError({
          errorLevel: 'error',
          properties: {
            handle,
            message: `Failed to fetch storytelling for ${handle}`,
            error
          }
        })
      })
  }

  return productTypesStorytelling
}

async function fetchProductDiscount(productTypes: string[] = []) {
  const discountByProductType: DiscountByProductType = {}
  // eslint-disable-next-line no-restricted-syntax
  for (const productType of productTypes) {
    // eslint-disable-next-line no-await-in-loop
    const productDiscountResponse = await fetchProductDiscountDescription({
      productType
    })
    const discount = getValueFromObject<ProductDiscountItem | null>(
      productDiscountResponse,
      'productDiscount.items[0]',
      null
    )
    discountByProductType[productType] = discount
      ? {
          title: discount.discountTitle,
          description: discount.discountDescription
        }
      : {}
  }
  return discountByProductType
}

async function fetchProductCareCopy(productType: string, preview = false) {
  const productCareCopy = await fetchContenfulProductCareCopy({
    productType,
    preview
  })
  return getValueFromObject<ContentfulDocument | null>(
    productCareCopy,
    'productCareCopy.items[0].copy.json',
    null
  )
}

function createProductsData(
  productTypesData: ProductByType[],
  discountByProductType: DiscountByProductType
) {
  return productTypesData
    .map(productTypeData => {
      if (!productTypeData.products?.length) {
        handleProductsBuildError({
          errorLevel: 'warning',
          properties: {
            message: `Missing CAS products - ${productTypeData.product_type}`
          }
        })
        return null
      }

      const updatedProducts = productTypeData.products.map(product => {
        const productTags = Array.isArray(product.tags)
          ? product.tags?.join()
          : product.tags
        const hasDiscount = product.tags.includes('multibuy')
        const discount = hasDiscount
          ? discountByProductType[product.product_type]
          : {}

        return {
          id: Number(product.id),
          title: product.title,
          body_html: product.body_html,
          product_type: product.product_type,
          byline: product.collection?.byline,
          handle: product.handle,
          price: product.price,
          compare_at_price: product.compare_at_price,
          options: transformProductOptions(product.options),
          images: product.images,
          image: product.image,
          product_feed: product.product_feed,
          meta: product.meta,
          color: product.color || null,
          tags: productTags,
          discount
        }
      })
      return {
        ...productTypeData,
        products: updatedProducts
      }
    })
    .filter(item => !!item)
}

type ProductPageProps = {
  productCategory: string
  productType: string
  statusCode: number
} & ProductListProps

function ProductsPage(props: ProductPageProps) {
  const [contentfulPreviewData, setContentfulPreviewData] = useState({})
  const { query } = useRouter()
  const productHandle = Array.isArray(query.productHandle)
    ? query.productHandle[0]
    : query.productHandle
  const productProps = { ...props, ...contentfulPreviewData }

  useEffect(() => {
    const fetchPreview = async (handle: string) => {
      try {
        const allRequests = [
          getProductsBreakdown(props.productType, props.productCategory, true),
          fetchProductCareCopy(props.productType, true),
          fetchStorytelling({
            productTypes: props.productTypes,
            handle,
            isPreview: true
          })
        ]
        const [productsBreakdown, careCopy, productTypesStorytelling] =
          await Promise.all(allRequests)

        setContentfulPreviewData({
          productsBreakdown,
          careCopy,
          productTypesStorytelling
        })
      } catch (e) {
        console.error(e)
      }
    }
    if (query.preview === 'true' && productHandle) {
      fetchPreview(productHandle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.preview, productHandle])

  if (!props.productsList?.length || props.statusCode === 404) {
    return <NotFound />
  }

  if (props.statusCode !== 200) {
    return <NextError statusCode={props.statusCode} />
  }

  if (props.productType === 'Gift Card') {
    return <GiftCard {...productProps} />
  }

  return <ProductList {...productProps} />
}

function productHandlesManager() {
  let handles: ProductHandles

  return {
    get: async function () {
      if (!handles) {
        handles = ((await getProductHandles()) || {}) as ProductHandles
      }

      return handles
    }
  }
}
const handlesManager = productHandlesManager()
const ratingsHandler = yotpoRatingsHandler()

export async function getStaticPaths() {
  const { activeHandles, inactiveHandles } = await handlesManager.get()

  if (inactiveHandles.length > 0) {
    handleProductsBuildError({
      errorLevel: 'warning',
      properties: {
        inactiveHandles,
        message: 'Inactive Handles'
      }
    })
  }

  const paths = activeHandles.map(product => ({
    params: { productHandle: product.handle }
  }))

  return { paths, fallback: false }
}

export async function getStaticProps({
  params
}: {
  params: { productHandle: string }
}) {
  const { productHandle } = params
  let productType: string | undefined = undefined

  try {
    const { activeHandles } = await handlesManager.get()
    const productData = activeHandles?.find(
      product => product.handle === productHandle
    )
    productType = productData?.product_type || ''
    const groupByTag = productData?.group_tag || ''

    const pageData = await getProductsByType(productType, groupByTag)

    const { productTypes: productTypesData, tabsOrder, errors } = pageData || {}

    if (errors) {
      throw new CustomError(`API error - ${JSON.stringify(errors)}`)
    }

    if (!productTypesData) {
      throw new CustomError(
        `CAS data is missing - ${productType} - ${productHandle}`
      )
    }

    if (tabsOrder?.length && tabsOrder?.length !== productTypesData.length) {
      throw new CustomError(
        `CAS: tabs order exists but length is different than product types length`
      )
    }

    const productTypes = productTypesData.map(
      (item: Record<string, unknown>) => item.product_type
    )

    const discountByProductType = await fetchProductDiscount(productTypes)

    const productsData = createProductsData(
      productTypesData,
      discountByProductType
    )

    if (!productsData?.length) {
      throw new Error(`All product types data is missing`)
    }

    const allProducts = productsData.flatMap(
      dataProductType => dataProductType?.products
    )

    const currentProductData = allProducts.find(
      item => item?.handle === productHandle
    )

    if (!currentProductData) {
      throw new CustomError(
        `Can't find data for this page. Product type has other products data but missing for this product handle`
      )
    }

    if (
      !currentProductData.color?.primary &&
      !PRODUCT_TYPES_WITHOUT_COLORS.includes(productType)
    ) {
      throw new CustomError(`Missing color data - ${productType}`)
    }
    const currentProductTypeData = productsData.find(
      data => data?.product_type === productType
    )

    if (
      currentProductTypeData?.color_categories?.length &&
      !currentProductData.color?.category
    ) {
      throw new CustomError(
        `Product color not included in a category - ${productType}`
      )
    }

    const productCategory = getProductCategory(
      currentProductData.options,
      productType
    )
    const productsBreakdown =
      (await getProductsBreakdown(productType, productCategory || '').catch(
        error => {
          handleProductsBuildError({
            errorLevel: 'error',
            properties: {
              handle: productHandle,
              message: `${error.name} - ${error.message}`,
              error
            }
          })
          return { props: { categories: { items: [] } } }
        }
      )) || null

    const productCareCopy = await fetchProductCareCopy(productType)

    const productTypesStorytelling = await fetchStorytelling({
      productTypes,
      handle: productHandle
    })

    const yotpoProductType = formatGroupType(productType)
    const yotpoProductRatings =
      (await ratingsHandler.getByGroup())[yotpoProductType] || {}

    const tabsWithProductsData = tabsOrder
      ?.map((tab: string) => {
        return productsData.find(
          productTypeItem => productTypeItem?.tab_title === tab
        )
      })
      .filter((tab?: string) => !!tab)

    const productsList =
      tabsWithProductsData?.length === productsData?.length
        ? tabsWithProductsData
        : productsData

    const productIds = allProducts.map(product => product?.id)

    const productProps = {
      productCategory,
      productsList,
      productsBreakdown,
      productIds,
      productType,
      productTypes,
      productTypesStorytelling,
      meta: currentProductData.meta
    }

    return {
      props: {
        ...productProps,
        careCopy: productCareCopy,
        starRatings: yotpoProductRatings,
        statusCode: 200
      }
    }
  } catch (e) {
    const error = e as typeof CustomError | AxiosError
    const handle = productHandle

    if (
      (error instanceof CustomError && error.status === 404) ||
      (axios.isAxiosError(error) && error.response?.status === 404)
    ) {
      handleProductsBuildError({
        errorLevel: 'warning',
        properties: {
          handle,
          message: error.message || '404 - Product does not exist'
        }
      })
    } else {
      const errorMessage =
        (error instanceof CustomError && error.message) ||
        (axios.isAxiosError(error) && error.response?.data?.message)
      handleProductsBuildError({
        errorLevel: 'critical',
        properties: {
          handle,
          productType: productType,
          ...(axios.isAxiosError(error) && error.response
            ? {
                path: error.request.path,
                error:
                  error?.response.data.error || error?.response.data.message
              }
            : { error: errorMessage })
        }
      })
      if (process.env.NODE_ENV === 'production' && isProdEnv()) {
        throw new Error(`Product page failed to export - ${handle}`)
      }
    }

    return {
      props: {
        statusCode:
          axios.isAxiosError(error) && error.response
            ? error.response.status
            : 404
      }
    }
  }
}

export default ProductsPage
